import { Form, Formik } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Modal, Stack } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AppContext } from '../../../../../appContext/AppContext';
import CustomTabMenu from '../../../../../component/CustomTabMenu';
import { TableCustom } from '../../../../../component/table/table-custom/TableCustom';
import TextField from '../../../../../component/TextField';
import { RESPONSE_MESSAGE } from '../../../../../utils/Constant';
import useReadQRCode from '../../../../hooks/useReadQRCode';
import { InsuranceVerifyParams } from '../../../../models/TiepDonModel';
import { verifyHealthInsurance } from '../../../../services/TiepNhanServices';
import { INSURANCE_CHECK_RESULT_COLUMNS } from './Columns';
import { CHECK_INSURANCE_FIELDS, initialVerifyResult, MA_KET_QUA } from './constants';
import CheckHistory from './tabs/CheckHistory';
import MedicalHistory from './tabs/MedicalHistory';
import {checkAddress} from "../../../../util/FunctionUtil";

type Props = {
	open: boolean;
	patientInfo?: any;
	onClose: () => void;
	onUpdatePatientDataFromInsurance?: (patientData: any, isLookedUp?: boolean) => void;
};

const CheckInfoDialog = ({
	open,
	onClose,
	patientInfo,
	onUpdatePatientDataFromInsurance,
}: Props) => {
	const { patient = {}, insurance = {} } = patientInfo;

	const { setIsLoading } = useContext(AppContext);
	const { getAddressInfo, getBirthDateInfo, getGenderInfo } = useReadQRCode();

	const [result, setResult] = useState<any>({
		verifyResult: initialVerifyResult,
		medicalHistory: [],
		verifyHistory: [],
		otherInfo: '',
	});

	const insurancePortalDataRef = useRef<any>();

	const TABS = [
		{
			eventKey: 'medical-history',
			title: 'Lịch sử KCB',
			component: <MedicalHistory data={result.medicalHistory} />,
		},
		{
			eventKey: 'verify-history',
			title: 'Lịch sử kiểm tra',
			component: <CheckHistory data={result.verifyHistory} />,
		},
	];

	useEffect(() => {
    const maThe = [
      insurance.insCodeDoiTuong,
      insurance.insCodeMucHuong,
      insurance.insCodeNoiPhatHanh,
      insurance.insCodeMaBhxh,
    ].join('')
      || patient.person?.idNumber
		if (!patientInfo || !patient.person?.fullName || !maThe) {
			return;
		}

		getVerifyInsuranceResult({
			hoTen: patient.person?.fullName,
			maThe,
			ngaySinh: getDateValue(
				patient.person?.dobDay,
				patient.person?.dobMonth,
				patient.person?.dobYear
			),
		});
	}, []);

	const getVerifyInsuranceResult = async (params: InsuranceVerifyParams) => {
		try {
			setIsLoading(true);
			const { data } = await verifyHealthInsurance(params);

			const patientInsurance = {
				hoTen: data.hoTen,
				gioiTinh: data.gioiTinh,
				ngaySinh: data.ngaySinh,
				diaChi: data.diaChi,
				maDKBD: data.maDKBD,
				coQuanBHXH: data.cqBHXH,
				giaTriTheTu: data.gtTheTu,
				giaTriTheDen: data.gtTheDen,
				maKhuVuc: data.maKV,
				ngayDu5Nam: data.ngayDu5Nam,
			};
			const patientInput = convertInputValuesToRowData();

			const verifyResult = CHECK_INSURANCE_FIELDS.map(
				(item: { field: string; title: string }) => ({
					fieldName: item.title,
					patientInput: patientInput[item.field as keyof typeof patientInput],
					patientInsurance: patientInsurance[item.field as keyof typeof patientInsurance],
					result: compareRowValue(
						patientInput[item.field as keyof typeof patientInput],
						patientInsurance[item.field as keyof typeof patientInsurance]
					),
				})
			);

			setResult({
				verifyResult,
				medicalHistory: data.dsLichSuKCB2018 ?? [],
				verifyHistory: data.dsLichSuKT2018 ?? [],
				otherInfo: {
					ketQua: getStringValueWithDelimiter([data.maKetQua, data.tenKetQua], '-'),
					ghiChu: data.ghiChu,
					maTheMoi: data.maTheMoi,
					hanTheMoi: getStringValueWithDelimiter(
						[data.gtTheTuMoi, data.gtTheDenMoi],
						'->'
					),
					maDKBDMoi: getStringValueWithDelimiter([data.maDKBDMoi, data.tenDKBDMoi], '->'),
				},
			});
			insurancePortalDataRef.current = data;
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const convertInputValuesToRowData = () => {
		return {
			hoTen: patient.person?.fullName,
			gioiTinh: patient.person?.gender?.name,
			ngaySinh: getDateValue(
				patient.person?.dobDay,
				patient.person?.dobMonth,
				patient.person?.dobYear
			),
			diaChi: getAddressValue(patient),
			maDKBD: insurance.insKcbbdCode,
			coQuanBHXH: '',
			giaTriTheTu: getDateValue(
				insurance.insFromDay,
				insurance.insFromMonth,
				insurance.insFromYear
			),
			giaTriTheDen: getDateValue(
				insurance.insToDay,
				insurance.insToMonth,
				insurance.insToYear
			),
			maKhuVuc: '',
			ngayDu5Nam: getDateValue(
				insurance.insNgaydu5namDay,
				insurance.insNgaydu5namMonth,
				insurance.insNgaydu5namYear
			),
		};
	};

	const getDateValue = (day: string, month: string, year: string) => {
		return [day, month, year].filter(item => item).join('/');
	};

	const getAddressValue = (patientValue: any) => {
		return [
			patientValue.address,
			patientValue.commune?.name,
			patientValue.district?.name,
			patientValue.province?.name,
		]
			.filter(item => item)
			.join(', ');
	};

	const getStringValueWithDelimiter = (value: any[], delimiter: string) => {
		return value.filter(item => item).join(` ${delimiter} `);
	};

	const compareRowValue = (source: string, target: string) => {
		if (!source || !target) return false;

		return source.trim().toLowerCase().localeCompare(target.trim().toLowerCase(), 'vi') === 0;
	};

	const getPatientInfoFromInsurancePortal = async () => {
		if (!insurancePortalDataRef.current?.hoTen) {
			toast.error("Không có thông tin thẻ trên cổng BHXH");
			return;
		}

		const { dsLichSuKCB2018, dsLichSuKT2018, ...data } = insurancePortalDataRef.current;
    const { ngaySinh, thangSinh, namSinh } = getBirthDateInfo(data.ngaySinh);
    const diaChiConverted = checkAddress(data?.diaChi, true);
    const genderOption = await getGenderInfo(data.gioiTinh);
    const { town, wardOption, districtOption, provinceOption } = await getAddressInfo(diaChiConverted);

    const insInfo = {
      ...data,
      genderOption,
      ngaySinh,
      thangSinh,
      namSinh,
      town,
      wardOption,
      districtOption,
      provinceOption,
    };

    onUpdatePatientDataFromInsurance?.(insInfo, true);
	};

	return (
		<Modal
			scrollable
			centered
			dialogClassName="h-modal modal-80w"
			size="xl"
			show={open}
			onHide={onClose}
			backdrop="static"
		>
			<Modal.Header closeButton>
				<Modal.Title>Kiểm tra thẻ BHYT</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div>
					<TableCustom
						data={result.verifyResult}
						columns={INSURANCE_CHECK_RESULT_COLUMNS}
						maxHeight={370}
					/>
				</div>

				<div className="spaces mt-10">
					<hr className="w-25 border-primary" />
					<Formik initialValues={{}} onSubmit={() => {}}>
						<Form>
							<Stack direction="horizontal" className="spaces gap-10">
								<TextField
									label="Mã thẻ mới:"
									variant="dotted"
									value={result.otherInfo?.maTheMoi}
									name="maTheMoi"
									readOnly
									className="spaces min-w-250 pe-none"
								/>
								<TextField
									label="Hạn thẻ mới:"
									variant="dotted"
									value={result.otherInfo?.hanTheMoi}
									name="hanTheMoi"
									readOnly
									className="spaces min-w-250 pe-none"
								/>
								<TextField
									label="Mã ĐKBĐ mới:"
									variant="dotted"
									value={result.otherInfo?.maDKBDMoi}
									name="maDKBDMoi"
									readOnly
									className="pe-none flex-1"
								/>
							</Stack>
							<TextField
								as="textarea"
								label="Ghi chú:"
								variant="dotted"
								value={result.otherInfo?.ghiChu}
								name="ghiChu"
								readOnly
								className={`spaces min-h-50 pt-8 pe-none resize-none ${
									MA_KET_QUA.HOP_LE !== result.otherInfo?.ketQua
										? 'text-danger'
										: 'text-green'
								}`}
							/>
						</Form>
					</Formik>
				</div>

				<div className="spaces pt-20 min-h-200">
					<CustomTabMenu danhsachTabs={TABS} />
				</div>
			</Modal.Body>

			<Modal.Footer>
				<Button className="btn-fill" onClick={() => getPatientInfoFromInsurancePortal()}>
					Cập nhật thông tin
				</Button>
				<Button className="btn-danger" onClick={onClose}>
					Đóng
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default CheckInfoDialog;
